import apiClient from './api';

/**
 * The layer screens' server (Veramenta docs/en/60 sec.10): our layers as the registry describes them, the
 * companies that see them, and GeoServer told what the registry says. For the platform's data team.
 */

export type Labels = Record<string, string>;

export interface StyleClass {
  equals?: string | null;
  from?: number | null;
  to?: number | null;
  fill?: string | null;
  stroke?: string | null;
  labels?: Labels;
}

export interface LayerStyle {
  mode: 'simple' | 'classes' | 'sld' | 'named';
  fill?: string | null;
  fillOpacity?: number | null;
  stroke?: string | null;
  strokeWidth?: number | null;
  marker?: string | null;
  size?: number | null;
  by?: string | null;
  classes?: StyleClass[];
  labelColour?: string | null;
  labelSize?: number | null;
  sld?: string | null;
}

export interface Measure {
  field: string;
  method: 'portion' | 'weighted-mean' | 'weighted-percent' | 'weighted-percentage' | 'weighted-value';
  weight?: string | null;
  group?: string | null;
  position?: number | null;
}

export interface MapLayer {
  key: string;
  type: string;
  labels: Labels;
  menu?: string | null;
  position?: number | null;
  kind?: string | null;
  visible?: boolean | null;
  minScale?: number | null;
  maxScale?: number | null;
  geoserver?: string | null;
  source?: { schema?: string; table?: string; geometry?: string; id?: string; select?: string; srid?: number } | null;
  style?: LayerStyle | null;
  info?: string[];
  label?: string | null;
  analysis?: Measure[];
  columns?: Record<string, string>;
}

export interface LayerSummary {
  key: string;
  number: number;
  typeKey: string;
  name: string;
  workspace: string;
  labels: Labels;
  menu?: string | null;
  kind?: string | null;
  position: number;
  binding: 'dynamic' | 'native' | null;
  table?: string | null;
  style?: string | null;
  fields: number;
  measures: number;
  companies: number;
}

export interface LayerField {
  key: string;
  column: string;
  type: string;
  of?: string | null;
  labels: Labels;
  fieldClass: 'none' | 'money' | 'contact' | 'pii' | 'premium';
}

export interface LayerDetail {
  key: string;
  number: number;
  typeKey: string;
  name: string;
  workspace: string;
  binding: 'dynamic' | 'native' | null;
  map: MapLayer;
  fields: LayerField[];
  geometry: 'point' | 'line' | 'area' | 'any';
  companies: string[];
  sld?: string | null;
  geoserver?: Published;
  /** The layer says no measures: a catchment adds up what its columns suggest (see suggestedMeasures). */
  analysisSuggested?: boolean;
}

export interface Published {
  layer: string;
  done: string[];
  problems: string[];
}

export interface LayerChange {
  labels?: Labels;
  menu?: string | null;
  position?: number | null;
  kind?: string | null;
  visible?: boolean | null;
  minScale?: number | null;
  maxScale?: number | null;
  style?: LayerStyle | null;
  info?: string[];
  label?: string | null;
  analysis?: Measure[];
  fields?: { key: string; labels?: Labels; fieldClass?: string }[];
}

export interface CompanySettings {
  tenant: string;
  name: string;
  driveCredits: number;
  walkCredits: number;
  motorCredits: number;
  logo?: string | null;
  mapLon?: number | null;
  mapLat?: number | null;
  mapZoom?: number | null;
  layers?: number;
  grants?: Record<string, string[]>;
}

export interface Vocabulary {
  menus: Record<string, number>;
  subMenus: Record<string, number>;
  kinds: string[];
  styles: string[];
  markers: string[];
  methods: string[];
  fieldClasses: string[];
}

export interface ImportReport {
  applied: boolean;
  layers: number;
  dynamic: number;
  nativeLayers: number;
  skipped: number;
  companies: number;
  grants: number;
  registryVersion?: number | null;
  found: { number: number; name: string; typeKey?: string; binding: string; source?: string; rows: number; columns: number; style?: string; problems: string[] }[];
  problems: string[];
}

const BASE = '/v2/admin';

export const layerAdmin = {
  async layers(): Promise<LayerSummary[]> {
    return (await apiClient.get<LayerSummary[]>(`${BASE}/layers`)).data;
  },
  async layer(key: string): Promise<LayerDetail> {
    return (await apiClient.get<LayerDetail>(`${BASE}/layers/${encodeURIComponent(short(key))}`)).data;
  },
  async change(key: string, change: LayerChange): Promise<LayerDetail> {
    return (await apiClient.put<LayerDetail>(`${BASE}/layers/${encodeURIComponent(short(key))}`, change)).data;
  },
  async publish(key: string): Promise<Published> {
    return (await apiClient.post<Published>(`${BASE}/layers/${encodeURIComponent(short(key))}/publish`)).data;
  },
  async sld(key: string, style: LayerStyle): Promise<string> {
    return (await apiClient.post<string>(`${BASE}/layers/${encodeURIComponent(short(key))}/sld`, style, { responseType: 'text' })).data;
  },
  /** What a catchment would add up from a layer by its columns' comments and names: to check, change and save. */
  async suggestedMeasures(key: string): Promise<Measure[]> {
    return (await apiClient.get<Measure[]>(`${BASE}/layers/${encodeURIComponent(short(key))}/analysis/suggested`)).data;
  },
  async vocabulary(): Promise<Vocabulary> {
    return (await apiClient.get<Vocabulary>(`${BASE}/menus`)).data;
  },
  async companies(): Promise<CompanySettings[]> {
    return (await apiClient.get<CompanySettings[]>(`${BASE}/companies`)).data;
  },
  async company(tenant: string): Promise<CompanySettings> {
    return (await apiClient.get<CompanySettings>(`${BASE}/companies/${tenant}`)).data;
  },
  async saveCompany(tenant: string, settings: Partial<CompanySettings>): Promise<CompanySettings> {
    return (await apiClient.put<CompanySettings>(`${BASE}/companies/${tenant}`, settings)).data;
  },
  async grant(tenant: string, grants: Record<string, string[]>): Promise<CompanySettings> {
    return (await apiClient.put<CompanySettings>(`${BASE}/companies/${tenant}/layers`, grants)).data;
  },
  async importLayers(apply: boolean, copy: boolean): Promise<ImportReport> {
    return (await apiClient.post<ImportReport>(`${BASE}/import`, null, { params: { apply, copy }, timeout: 30 * 60 * 1000 })).data;
  },
};

function short(key: string): string {
  return key.startsWith('map-layer.') ? key.substring('map-layer.'.length) : key;
}

/** A label in the language asked, else English, else any. */
/** A label in the given language, else English, else Turkish, else any, else the fallback. */
export function labelOf(labels: Labels | undefined, lang: string, fallback = ''): string {
  if (!labels) return fallback;
  return labels[lang] || labels.en || labels.tr || Object.values(labels).find((v) => !!v) || fallback;
}

export default layerAdmin;
